import { useEffect, useMemo, useState } from "react";
import {
  createInventoryItem,
  deactivateInventoryItem,
  listInventoryCategories,
  listInventoryItems,
  listStockBalances,
  listStockMovements,
  updateInventoryItem,
  type AdminSession,
  type InventoryCategoryItem,
  type InventoryItemOption,
  type InventoryMovementItem,
} from "@/lib/api";
import Card from "../components/Card";

type ProductForm = {
  categoryId: string;
  sku: string;
  name: string;
  unit: string;
  minimumStock: number;
};

type EditProductForm = ProductForm & { active: boolean };

const emptyProductForm: ProductForm = {
  categoryId: "",
  sku: "",
  name: "",
  unit: "unidad",
  minimumStock: 0,
};

const emptyEditProductForm: EditProductForm = { ...emptyProductForm, active: true };

export default function ProductosSection({ session }: { session: AdminSession }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [categories, setCategories] = useState<InventoryCategoryItem[]>([]);
  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [stockByItemId, setStockByItemId] = useState<Map<string, number>>(new Map());

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [productForm, setProductForm] = useState<ProductForm>(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState("");
  const [editProductForm, setEditProductForm] = useState<EditProductForm>(emptyEditProductForm);

  const [historyItemId, setHistoryItemId] = useState("");
  const [historyItemLabel, setHistoryItemLabel] = useState("");
  const [historyMovements, setHistoryMovements] = useState<InventoryMovementItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTypeFilter, setHistoryTypeFilter] = useState("all");
  const [historyFromDate, setHistoryFromDate] = useState("");
  const [historyToDate, setHistoryToDate] = useState("");

  async function load(currentSession: AdminSession): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const [catData, itemData, balanceData] = await Promise.all([
        listInventoryCategories(currentSession),
        listInventoryItems(currentSession),
        listStockBalances(currentSession),
      ]);
      setCategories(catData);
      setItems(itemData);
      const map = new Map<string, number>();
      balanceData.forEach((balance) => {
        map.set(balance.inventoryItemId, (map.get(balance.inventoryItemId) ?? 0) + balance.quantityOnHand);
      });
      setStockByItemId(map);
      setProductForm((prev) => ({ ...prev, categoryId: prev.categoryId || catData[0]?.id || "" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar productos");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(session);
  }, [session]);

  const rows = useMemo(() => {
    return items
      .map((item) => ({ ...item, stock: stockByItemId.get(item.id) ?? 0 }))
      .filter((row) => {
        const query = search.trim().toLowerCase();
        const matchesSearch =
          !query ||
          row.name.toLowerCase().includes(query) ||
          row.sku.toLowerCase().includes(query) ||
          (row.categoryName ?? "").toLowerCase().includes(query);
        const matchesCategory = categoryFilter === "all" || row.categoryId === categoryFilter;
        return matchesSearch && matchesCategory;
      });
  }, [items, stockByItemId, search, categoryFilter]);

  const filteredHistory = useMemo(() => {
    return historyMovements.filter((movement) => {
      const matchesType = historyTypeFilter === "all" || movement.movementType === historyTypeFilter;
      const movementDate = movement.createdAt ? new Date(movement.createdAt) : null;
      const matchesFrom = !historyFromDate || (movementDate && movementDate >= new Date(`${historyFromDate}T00:00:00`));
      const matchesTo = !historyToDate || (movementDate && movementDate <= new Date(`${historyToDate}T23:59:59`));
      return matchesType && Boolean(matchesFrom) && Boolean(matchesTo);
    });
  }, [historyMovements, historyTypeFilter, historyFromDate, historyToDate]);

  async function handleCreateProduct(): Promise<void> {
    if (!productForm.categoryId || !productForm.sku.trim() || !productForm.name.trim()) {
      setError("Completa categoría, SKU y nombre para crear el producto.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createInventoryItem(session, {
        categoryId: productForm.categoryId,
        sku: productForm.sku.trim(),
        name: productForm.name.trim(),
        unit: productForm.unit.trim() || "unidad",
        minimumStock: Number(productForm.minimumStock || 0),
      });
      setProductForm({ ...emptyProductForm, categoryId: productForm.categoryId });
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear producto");
    } finally {
      setSaving(false);
    }
  }

  function handleStartEditProduct(row: InventoryItemOption): void {
    setEditingProductId(row.id);
    setEditProductForm({
      categoryId: row.categoryId || "",
      sku: row.sku,
      name: row.name,
      unit: row.unit,
      minimumStock: Number(row.minimumStock ?? 0),
      active: row.active,
    });
  }

  async function handleSaveEditProduct(): Promise<void> {
    if (!editingProductId) return;
    if (!editProductForm.categoryId || !editProductForm.sku.trim() || !editProductForm.name.trim()) {
      setError("Completa categoría, SKU y nombre para guardar.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateInventoryItem(session, editingProductId, {
        categoryId: editProductForm.categoryId,
        sku: editProductForm.sku.trim(),
        name: editProductForm.name.trim(),
        unit: editProductForm.unit.trim() || "unidad",
        minimumStock: Number(editProductForm.minimumStock || 0),
        active: editProductForm.active,
      });
      setEditingProductId("");
      setEditProductForm(emptyEditProductForm);
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar producto");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivateProduct(row: InventoryItemOption): Promise<void> {
    const confirmed = window.confirm(`¿Desactivar producto ${row.sku} - ${row.name}?`);
    if (!confirmed) return;
    setSaving(true);
    setError("");
    try {
      await deactivateInventoryItem(session, row.id);
      if (editingProductId === row.id) {
        setEditingProductId("");
        setEditProductForm(emptyEditProductForm);
      }
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo desactivar producto");
    } finally {
      setSaving(false);
    }
  }

  async function handleReactivateProduct(row: InventoryItemOption): Promise<void> {
    setSaving(true);
    setError("");
    try {
      await updateInventoryItem(session, row.id, { active: true });
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo reactivar producto");
    } finally {
      setSaving(false);
    }
  }

  async function handleOpenHistory(row: InventoryItemOption): Promise<void> {
    setHistoryItemId(row.id);
    setHistoryItemLabel(`${row.sku} · ${row.name}`);
    setHistoryTypeFilter("all");
    setHistoryFromDate("");
    setHistoryToDate("");
    setHistoryLoading(true);
    try {
      const data = await listStockMovements(session, { inventoryItemId: row.id, limit: 120 });
      setHistoryMovements(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cargar historial de movimientos");
    } finally {
      setHistoryLoading(false);
    }
  }

  function handleExportHistoryCsv(): void {
    const headers = ["fecha", "bodega", "tipo", "cantidad", "costo_unitario", "referencia", "notas"];
    const lines = filteredHistory.map((movement) => {
      const row = [
        movement.createdAt ? new Date(movement.createdAt).toLocaleString("es-CL") : "",
        `${movement.warehouseCode || ""} ${movement.warehouseName || ""}`.trim(),
        formatMovementType(movement.movementType),
        String(movement.quantity),
        String(movement.unitCost ?? 0),
        `${movement.referenceType || ""}/${movement.referenceId || ""}`,
        movement.notes || "",
      ];
      return row.map(csvEscape).join(",");
    });
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `historial_${historyItemId || "producto"}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-4">
          <Card title="Nuevo Producto">
            <div className="space-y-2">
              <select value={productForm.categoryId} onChange={(e) => setProductForm((p) => ({ ...p, categoryId: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <option value="">Categoría</option>
                {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
              <input value={productForm.sku} onChange={(e) => setProductForm((p) => ({ ...p, sku: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="SKU" />
              <input value={productForm.name} onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Nombre" />
              <select value={productForm.unit} onChange={(e) => setProductForm((p) => ({ ...p, unit: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <option value="unidad">Unidad (un)</option>
                <option value="kg">Kilogramo (kg)</option>
                <option value="mt">Metro (mt)</option>
              </select>
              <input type="number" value={productForm.minimumStock} onChange={(e) => setProductForm((p) => ({ ...p, minimumStock: Number(e.target.value || 0) }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Stock mínimo" />
            </div>
            <div className="mt-2 flex justify-end">
              <button disabled={saving} onClick={() => void handleCreateProduct()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">Crear producto</button>
            </div>
          </Card>

          {editingProductId ? (
            <Card title="Editar Producto">
              <div className="space-y-2">
                <select value={editProductForm.categoryId} onChange={(e) => setEditProductForm((p) => ({ ...p, categoryId: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <option value="">Categoría</option>
                  {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                </select>
                <input value={editProductForm.sku} onChange={(e) => setEditProductForm((p) => ({ ...p, sku: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="SKU" />
                <input value={editProductForm.name} onChange={(e) => setEditProductForm((p) => ({ ...p, name: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Nombre" />
                <select value={editProductForm.unit} onChange={(e) => setEditProductForm((p) => ({ ...p, unit: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <option value="unidad">Unidad (un)</option>
                  <option value="kg">Kilogramo (kg)</option>
                  <option value="mt">Metro (mt)</option>
                </select>
                <input type="number" value={editProductForm.minimumStock} onChange={(e) => setEditProductForm((p) => ({ ...p, minimumStock: Number(e.target.value || 0) }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Stock mínimo" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={editProductForm.active} onChange={(e) => setEditProductForm((p) => ({ ...p, active: e.target.checked }))} />
                  Producto activo
                </label>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingProductId(""); setEditProductForm(emptyEditProductForm); }} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">Cancelar</button>
                  <button disabled={saving} onClick={() => void handleSaveEditProduct()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">Guardar cambios</button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-100 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <input value={search} onChange={(event) => setSearch(event.target.value)} className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Buscar por SKU, nombre o categoría..." />
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <option value="all">Todas las categorías</option>
                {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="text-sm text-gray-500">Cargando productos...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase text-gray-500">
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Producto</th>
                      <th className="px-3 py-2">Categoría</th>
                      <th className="px-3 py-2">Unidad</th>
                      <th className="px-3 py-2">Stock</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const minimum = Number(row.minimumStock ?? 0);
                      const low = row.stock <= minimum;
                      return (
                        <tr key={row.id} className="border-b border-gray-50 text-sm text-gray-700">
                          <td className="px-3 py-2 font-mono text-xs">{row.sku}</td>
                          <td className="px-3 py-2">{row.name}</td>
                          <td className="px-3 py-2">{row.categoryName || "-"}</td>
                          <td className="px-3 py-2">{row.unit}</td>
                          <td className="px-3 py-2">{row.stock}</td>
                          <td className="px-3 py-2">
                            {!row.active ? (
                              <span className="rounded-full bg-gray-200 px-2 py-1 text-xs text-gray-700">Inactivo</span>
                            ) : (
                              <span className={`rounded-full px-2 py-1 text-xs ${low ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {low ? "Bajo stock" : "OK"}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => void handleOpenHistory(row)} className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700">Historial</button>
                              <button onClick={() => handleStartEditProduct(row)} className="rounded-lg border border-blue-200 px-2 py-1 text-xs text-blue-700">Editar</button>
                              {row.active ? (
                                <button onClick={() => void handleDeactivateProduct(row)} className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700">Desactivar</button>
                              ) : (
                                <button onClick={() => void handleReactivateProduct(row)} className="rounded-lg border border-emerald-200 px-2 py-1 text-xs text-emerald-700">Reactivar</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-4 text-sm text-gray-500">No hay productos para este filtro.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {historyItemId ? (
        <Card title={`Historial de Movimientos · ${historyItemLabel}`}>
          {historyLoading ? (
            <div className="text-sm text-gray-500">Cargando historial...</div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                <select value={historyTypeFilter} onChange={(e) => setHistoryTypeFilter(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <option value="all">Todos los tipos</option>
                  <option value="in">Entrada</option>
                  <option value="out">Salida</option>
                  <option value="adjustment">Ajuste</option>
                </select>
                <input type="date" value={historyFromDate} onChange={(e) => setHistoryFromDate(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                <input type="date" value={historyToDate} onChange={(e) => setHistoryToDate(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                <button onClick={handleExportHistoryCsv} disabled={filteredHistory.length === 0} className="rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-700 disabled:opacity-50">Exportar CSV</button>
              </div>
              {filteredHistory.map((movement) => (
                <div key={movement.id} className="rounded-lg border border-gray-100 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-800">{movement.warehouseCode || "-"} · {movement.warehouseName || "Bodega"}</div>
                    <div className="text-xs text-gray-500">{movement.createdAt ? new Date(movement.createdAt).toLocaleString("es-CL") : "-"}</div>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {formatMovementType(movement.movementType)} · Cantidad: {movement.quantity}
                    {movement.unitCost > 0 ? ` · Costo: $${Math.round(movement.unitCost).toLocaleString("es-CL")}` : ""}
                  </div>
                  <div className="text-xs text-gray-500">
                    Referencia: {movement.referenceType || "-"} / {movement.referenceId || "-"}
                    {movement.personName ? ` · Persona: ${movement.personName}` : ""}
                    {movement.notes ? ` · ${movement.notes}` : ""}
                  </div>
                </div>
              ))}
              {filteredHistory.length === 0 ? <div className="text-sm text-gray-500">No hay movimientos para este filtro.</div> : null}
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}

function formatMovementType(value: string): string {
  if (value === "in") return "Entrada";
  if (value === "out") return "Salida";
  if (value === "adjustment") return "Ajuste";
  return value;
}

function csvEscape(value: string): string {
  const clean = value.replace(/"/g, "\"\"");
  return `"${clean}"`;
}
